import { makeEventNamespace } from "~/core/dom/events";
import { initSection } from "~/core/shopify/init-section";
import "~/styles/sections/mission-hero.css";

initSection(".js-mission-hero-section", (section) => {
    const namespace = makeEventNamespace();
    const root = section.querySelector<HTMLElement>("[data-mission-hero]");
    const tabs = Array.from(
        section.querySelectorAll<HTMLButtonElement>("[data-mission-tab]"),
    );
    const panels = Array.from(
        section.querySelectorAll<HTMLElement>("[data-mission-panel]"),
    );

    const tabList = section.querySelector<HTMLElement>("[data-mission-tabs]");
    const autoplay =
        root !== null &&
        root.hasAttribute("data-mission-autoplay") &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let activeIndex = 0;
    let focused = false;
    let offscreen = false;

    // The loop runs on a real progress element through the Web Animations API. iOS Safari never
    // started the CSS animation on the tab's ::after, so the old animationend timer never fired.
    const duration =
        Number(root?.getAttribute("data-mission-autoplay")) || 6000;
    const bars = autoplay
        ? tabs.map((tab) => {
              const bar = document.createElement("span");
              bar.className = "mission-hero__progress";
              bar.setAttribute("aria-hidden", "true");
              tab.append(bar);
              return bar;
          })
        : [];
    let progress: Animation | null = null;

    // A click restarts the chosen pillar's line and lets it run, so pointer hover and mouse focus
    // no longer pause the loop. Keyboard focus still does, and so does scrolling out of view.
    const isPaused = (): boolean => focused || offscreen;

    const syncPaused = (): void => {
        root?.setAttribute("data-paused", String(isPaused()));
        if (progress === null) return;
        if (isPaused()) {
            progress.pause();
        } else if (progress.playState === "paused") {
            progress.play();
        }
    };

    const startProgress = (): void => {
        progress?.cancel();
        const bar = bars[activeIndex];
        if (bar === undefined) return;
        progress = bar.animate(
            [{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }],
            { duration, easing: "linear", fill: "forwards" },
        );
        progress.onfinish = () => activate((activeIndex + 1) % tabs.length);
        if (isPaused()) progress.pause();
    };

    // Mobile scrolls the tab strip sideways; keep the active tab in view as the loop advances.
    const revealTab = (tab: HTMLElement | undefined): void => {
        if (tabList === null || tab === undefined) return;
        if (tabList.scrollWidth <= tabList.clientWidth) return;
        const offset =
            tab.getBoundingClientRect().left -
            tabList.getBoundingClientRect().left;
        tabList.scrollTo({
            left: tabList.scrollLeft + offset - 25,
            behavior: "smooth",
        });
    };

    function activate(index: number, moveFocus = false): void {
        activeIndex = index;
        revealTab(tabs[index]);
        tabs.forEach((tab, tabIndex) => {
            const active = tabIndex === index;
            tab.classList.toggle("is-active", active);
            tab.setAttribute("aria-selected", String(active));
            tab.tabIndex = active ? 0 : -1;
            if (active && moveFocus) tab.focus();
        });
        panels.forEach((panel, panelIndex) => {
            const active = panelIndex === index;
            panel.classList.toggle("is-active", active);
            panel.hidden = !active;
        });
        if (bars.length > 0) startProgress();
    }

    tabs.forEach((tab, index) => {
        namespace.addDirectEventListener(tab, "click", () => activate(index));
        namespace.addDirectEventListener(tab, "keydown", (event) => {
            if (!(event instanceof KeyboardEvent)) return;
            let nextIndex: number | null = null;
            if (event.key === "ArrowRight" || event.key === "ArrowDown") {
                nextIndex = (index + 1) % tabs.length;
            } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
                nextIndex = (index - 1 + tabs.length) % tabs.length;
            } else if (event.key === "Home") {
                nextIndex = 0;
            } else if (event.key === "End") {
                nextIndex = tabs.length - 1;
            }
            if (nextIndex === null) return;
            event.preventDefault();
            activate(nextIndex, true);
        });
    });

    let visibility: IntersectionObserver | null = null;
    if (autoplay && root !== null && tabList !== null) {
        namespace.addDirectEventListener(root, "focusin", (_root, evt) => {
            if (
                !(evt.target instanceof Element) ||
                !evt.target.matches(":focus-visible")
            ) {
                return;
            }
            focused = true;
            syncPaused();
        });
        namespace.addDirectEventListener(root, "focusout", (_root, evt) => {
            if (
                evt instanceof FocusEvent &&
                evt.relatedTarget instanceof Node &&
                root.contains(evt.relatedTarget)
            ) {
                return;
            }
            focused = false;
            syncPaused();
        });
        visibility = new IntersectionObserver(([entry]) => {
            offscreen = entry?.isIntersecting !== true;
            syncPaused();
        });
        visibility.observe(root);
    } else {
        root?.removeAttribute("data-mission-autoplay");
    }

    if (tabs.length > 0 && panels.length > 0) {
        root?.setAttribute("data-enhanced", "true");
        activate(0);
    }

    return {
        unload: () => {
            namespace.destroy();
            visibility?.disconnect();
            progress?.cancel();
            bars.forEach((bar) => bar.remove());
        },
        selectBlock: (block) => {
            const panel = block.closest<HTMLElement>("[data-mission-panel]");
            if (panel === null) return;
            const index = panels.indexOf(panel);
            if (index >= 0) activate(index);
        },
    };
});
